import { FunctionComponent, useEffect, useState } from "react";
import { Body1SemiBold, Body3, InputHeading, styled, SubHeading3 } from '@horizontal-org/shira-ui'
import { InputWithExplanation } from "../../../InputWithExplanation";
import { DraggableMessagingList } from "./DraggableMessagingList";
import { ActiveQuestion, MessagingContent as MessagingContentType } from "../../../../store/types/active_question";
import { useStore } from "../../../../store";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import { MessageEditorStyles } from "../../../TipTapEditor/styles/MessageEditorStyles";
import { SENDER_NAME_MAX_LENGTH, SENDER_PHONE_MAX_LENGTH } from "../../../../utils/inputLimits";

interface Props {
  question: ActiveQuestion
  content: MessagingContentType
}

const MessagingAppsNames = {
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  MESSENGER: 'Messenger',
  DATING_APP: 'Dating App'
}

export const MessagingContent: FunctionComponent<Props> = ({
  question,
  content,
}) => {

  const { t } = useTranslation();

  const {
    updateActiveQuestionDraggableItems
  } = useStore((state) => ({
    updateActiveQuestionDraggableItems: state.updateActiveQuestionDraggableItems
  }), shallow)

  const hasSenderPhone = () => {
    if (question && question.app) {
      return !!([
        MessagingAppsNames.SMS,
        MessagingAppsNames.WHATSAPP
      ].includes(question.app.name))
    }

    return false
  }

  const [senderPhoneEnabled, handleSenderPhoneEnabled] = useState<boolean>(false)

  useEffect(() => {
    handleSenderPhoneEnabled(hasSenderPhone)
  }, [])

  return (
    <Content id="messaging-content">

      {senderPhoneEnabled ? (
        <div>
          <InputHeading required>
            <SubHeading3 id="messaging-content-sender-phone-title">{t('create_question.tabs.content.sender_phone.title')}</SubHeading3>
            <Body3 id="messaging-content-sender-phone-subtitle">{t('create_question.tabs.content.sender_phone.subtitle')}</Body3>
          </InputHeading>

          <InputWithExplanation
            id='component-required-phone'
            name='senderPhone'
            placeholder={t('create_question.tabs.content.sender_phone.placeholder')}
            label="Sender phone number"
            contentObject={content.senderPhone}
            showCharacterCount={true}
            maxLength={SENDER_PHONE_MAX_LENGTH}
            characterLimitErrorText={t('error_messages.character_limit_error')}
          />
        </div>
      ) : (
        <div>
          <InputHeading required={!senderPhoneEnabled}>
            <SubHeading3 id="messaging-content-sender-title">{t('create_question.tabs.content.sender.title')}</SubHeading3>
            <Body3 id="messaging-content-sender-subtitle">{t('create_question.tabs.content.sender.subtitle')}</Body3>
          </InputHeading>

          <InputWithExplanation
            id='component-required-fullname'
            name='senderName'
            placeholder={t('create_question.tabs.content.sender.placeholder')}
            label="Sender name"
            contentObject={content.senderName}
            showCharacterCount={true}
            maxLength={SENDER_NAME_MAX_LENGTH}
            characterLimitErrorText={t('error_messages.character_limit_error')}
          />
        </div>
      )}

      <div>
        <MessagingContentHead>
          <Body1SemiBold id="messaging-content-message-content-title">
            {t('create_question.tabs.content.message_content.title')}
          </Body1SemiBold>
          <Body3 id="messaging-content-message-content-subtitle">
            {t('create_question.tabs.content.message_content.subtitle')}
          </Body3>
        </MessagingContentHead>

        <MessageEditorStyles />

        <DraggableMessagingList
          content={content}
          items={content.draggableItems}
          onChange={(newItems) => { updateActiveQuestionDraggableItems(newItems) }}
        />
      </div>

    </Content>
  )
}

const Content = styled.div`
  gap: 48px;
  display: flex;
  flex-direction: column;
`

const MessagingContentHead = styled.div`
  padding-bottom: 20px;
`
